import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Clock, Calendar } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import SEO from '../components/SEO';
import GridBackground from '../components/GridBackground';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloatButton from '../components/WhatsAppFloatButton';
import TableOfContents from '../components/blog/TableOfContents';
import ShareButtons from '../components/blog/ShareButtons';
import { slugifyHeading } from '../components/blog/blogCategories';
import Breadcrumbs from '@/components/Breadcrumbs';
import RelatedServiceBox from '@/components/blog/RelatedServiceBox';
import { ORG_REF, breadcrumbLd, authorLabel, isFounder } from '@/lib/structuredData';

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, isPending: loading, isError, refetch } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const data = await base44.entities.BlogPost.filter({ slug }, '-created_date', 1);
      return data[0] || null;
    },
    staleTime: 60000,
    retry: 1
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background text-foreground relative">
        <Navbar />
        <main className="pt-40 pb-24 px-6 text-center">
          <h1 className="text-3xl font-bold mb-4">Unable to load this article</h1>
          <p className="text-muted-foreground mb-6">Please check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-6 py-3 rounded-lg bg-primary text-primary-foreground">Try again</button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-foreground relative">
        <SEO title="Article Not Found" path={`/blog/${slug}`} noindex />
        <GridBackground />
        <Navbar />
        <div className="pt-40 pb-24 px-6 text-center">
          <h1 className="text-3xl font-inter font-bold text-foreground mb-4">Article not found</h1>
          <Link to="/blog" className="text-primary hover:underline font-inter">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');
  const formattedDate = fmt(post.published_date);
  const modifiedDate = (post.updated_date || post.published_date || '').slice(0, 10);
  const crumbs = [{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: post.title, to: `/blog/${post.slug}` }];

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <SEO
        title={post.title}
        fullTitle={post.meta_title ? `${post.meta_title} | Jotunheims` : undefined}
        lang={post.lang || 'en'}
        ogLocale={post.lang === 'es' ? 'es_ES' : 'en_US'}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              image: post.cover_image,
              datePublished: post.published_date,
              dateModified: modifiedDate,
              author: isFounder(post)
                ? { '@type': 'Person', name: 'Nelson M. Acevedo', jobTitle: 'Founder & CEO', url: 'https://jotunheims.com/about' }
                : { '@type': 'Organization', name: 'Jotunheims', url: 'https://jotunheims.com/about' },
              publisher: ORG_REF,
              mainEntityOfPage: `https://jotunheims.com/blog/${post.slug}`,
              inLanguage: post.lang || 'en'
            },
            breadcrumbLd(crumbs)
          ]
        }}
      />
      <GridBackground />
      <Navbar />

      <article className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto mb-10">
          <Breadcrumbs items={crumbs} />
          <span className="text-xs font-inter font-bold tracking-[0.2em] uppercase text-primary mb-4 block">
            {post.category}
          </span>
          <h1 className="font-inter font-black text-3xl md:text-5xl tracking-[-0.02em] text-foreground mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-muted-foreground font-inter">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formattedDate}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.reading_time || 5} min read</span>
            {modifiedDate && <span>Last updated {fmt(modifiedDate)}</span>}
            <span>By <Link to="/about" className="text-primary hover:underline">{authorLabel(post)}</Link></span>
          </div>
        </div>

        {post.cover_image && (
          <div className="max-w-5xl mx-auto mb-12">
            <img src={post.cover_image} alt={post.title} loading="lazy" className="w-full h-auto rounded-2xl border border-border/50" />
          </div>
        )}

        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_260px] gap-12">
          <div className="prose prose-invert max-w-none font-inter prose-headings:font-inter prose-headings:font-bold prose-p:text-muted-foreground prose-p:leading-relaxed prose-a:text-primary prose-strong:text-foreground prose-li:text-muted-foreground">
            <ReactMarkdown
              components={{
                h2: ({ children }) => (
                  <h2 id={slugifyHeading(String(children))} className="text-2xl md:text-3xl font-bold text-foreground mt-10 mb-4">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 id={slugifyHeading(String(children))} className="text-xl font-bold text-foreground mt-8 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => <p className="text-muted-foreground leading-relaxed mb-4">{children}</p>,
                a: ({ href, children }) => (
                  /^https?:\/\//.test(href || '') ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>
                  ) : /^(mailto:|tel:|#)/.test(href || '') ? (
                    <a href={href} className="text-primary hover:underline">{children}</a>
                  ) : (
                    <Link to={href || '/blog'} className="text-primary hover:underline">{children}</Link>
                  )
                )
              }}
            >
              {post.content}
            </ReactMarkdown>

            <div className="mt-10 pt-6 border-t border-border/30 flex items-center justify-between flex-wrap gap-4">
              <div className="flex flex-wrap gap-2">
                {(post.tags || []).map((tag) => (
                  <span key={tag} className="px-3 py-1.5 text-[10px] font-inter font-medium tracking-wider uppercase text-muted-foreground border border-border/30 rounded-lg bg-secondary/20">
                    {tag}
                  </span>
                ))}
              </div>
              <ShareButtons title={post.title} />
            </div>

            <RelatedServiceBox post={post} />

            {post.author_bio && (
              <div className="mt-10 p-6 rounded-2xl border border-border/50 bg-card">
                <p className="text-xs font-inter font-bold tracking-[0.2em] uppercase text-primary mb-2">About the Author</p>
                <p className="text-sm font-inter font-semibold text-foreground mb-1">{post.author_name}</p>
                <p className="text-sm text-muted-foreground font-inter leading-relaxed">{post.author_bio}</p>
              </div>
            )}
          </div>

          <aside className="hidden lg:block">
            <TableOfContents content={post.content} />
          </aside>
        </div>
      </article>

      <Footer />
      <WhatsAppFloatButton />
    </div>
  );
}